import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import DataContext from "../../../context/DataContext";
import PaginatorContext from "../../../context/PaginatorContext";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import { PumpEntryForm } from "../../../components/documents/pump/PumpEntryForm";
import { PumpStatusBadge } from "../../../components/documents/pump/PumpStatusBadge";
import { formatCOIDate, formatCOIInteger } from "../../../components/documents/coi/coiFormatters";
import { useFetch } from "../../../hooks/useFetch";
import { useRequest } from "../../../hooks/useRequest";
import {
  ClientProps,
  PumpEntryProps,
  PumpEntryStatus,
  PumpProps,
} from "../../../interfaces/interfaces";

const STATUS_OPTIONS: { value: PumpEntryStatus; label: string }[] = [
  { value: "in-stock", label: "In stock" },
  { value: "reused", label: "Reused" },
];

interface PumpEntryResponse {
  success?: boolean;
  data: PumpEntryProps[];
}

interface Props {
  apiPrefix: string;
  title: string;
  canManage: boolean;
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
}

const filterLabelSx = {
  display: "block",
  mb: "5px",
  color: "#9CA1AB",
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".6px",
  lineHeight: 1.2,
  textTransform: "uppercase",
};

const documentFilterSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    bgcolor: "#FAFAF8",
    borderRadius: "9px",
    fontSize: 13,
    "& fieldset": { borderColor: "#E2E2DD" },
    "&:hover fieldset": { borderColor: "#C9C7BE" },
    "&.Mui-focused fieldset": {
      borderColor: "#ED8B00",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": { py: "8px" },
};

export const PumpModuleAdmin = ({
  apiPrefix,
  title,
  canManage,
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
}: Props) => {
  const { paginationModel, fetchPaginationModel } = useContext(PaginatorContext);
  const {
    clientOptions: globalClientOptions,
    wellOptions,
    fetchDataClient,
    fetchDataWells,
  } = useContext(DataContext);
  const { handleRequest } = useRequest();
  const clientOptions = clientMode ? assignedClientOptions : globalClientOptions;

  const [clientId, setClientId] = useState("");
  const [pumpId, setPumpId] = useState("");
  const [pumps, setPumps] = useState<PumpProps[]>([]);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PumpEntryProps | null>(null);

  useEffect(() => {
    if (!clientMode) fetchDataClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    setClientId(String(assignedClientId));
    fetchDataWells(assignedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedClientId, clientMode]);

  useEffect(() => {
    if (!clientId) {
      setPumps([]);
      return;
    }
    let active = true;
    handleRequest({
      endpoint: `${apiPrefix}-pumps/client/${clientId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        if (active) setPumps(response.data || []);
      })
      .catch(() => {
        if (active) setPumps([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, apiPrefix]);

  const filters = useMemo(
    () => ({ clientId, pumpId, status, dateFrom, dateTo }),
    [clientId, pumpId, status, dateFrom, dateTo],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<PumpEntryResponse>(
      `${apiPrefix}-paginate`,
      paginationModel,
      undefined,
      undefined,
      filters,
    );
  const rows = data?.data || [];
  const resetPage = () => fetchPaginationModel({ ...paginationModel, page: 0 });

  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId ? String(assignedClientId) : value;
    setClientId(nextClientId);
    setPumpId("");
    if (clientMode) onAssignedClientChange?.(nextClientId ? Number(nextClientId) : null);
    if (nextClientId) fetchDataWells(Number(nextClientId));
    resetPage();
  };

  const filterText =
    (setter: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
      setter(event.target.value);
      resetPage();
    };

  const hasActiveFilters = Boolean(pumpId || status || dateFrom || dateTo);
  const clearFilters = () => {
    setPumpId("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    resetPage();
  };

  const updateRowStatus = async (row: PumpEntryProps, nextStatus: PumpEntryStatus) => {
    await handleRequest({
      endpoint: `${apiPrefix}/${row.id}/status`,
      options: { method: "PUT", body: JSON.stringify({ status: nextStatus }) },
    });
    fetchData();
  };

  const tableHeaders = [
    "Qty",
    "Pump",
    "Well",
    ...(clientMode ? [] : ["Client"]),
    "Pulled date",
    "Status",
    "Notes",
    ...(canManage ? [""] : []),
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F3F0", color: "#29313D" }}>
      <DocumentsTopNavbar
        title={title}
        actions={
          canManage ? (
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => setLogOpen(true)}
              sx={{
                borderColor: "#E7E7E3",
                color: "#2A2F38",
                borderRadius: "9px",
                px: "15px",
                py: "8px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
              }}
            >
              Log Pulled Pump
            </Button>
          ) : undefined
        }
      />
      <Box sx={{ maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }}>
        <Paper
          elevation={0}
          sx={{ mb: 2, border: "1px solid #E7E7E3", borderRadius: "14px", overflow: "hidden" }}
        >
          <Box
            sx={{ p: "16px 20px", display: "flex", alignItems: "flex-end", gap: "11px", flexWrap: "wrap" }}
          >
            <Box sx={{ width: { xs: "100%", sm: 190 } }}>
              <Box component="label" sx={filterLabelSx}>Client</Box>
              <Autocomplete
                size="small"
                options={clientOptions}
                value={clientOptions.find((client) => String(client.id) === clientId) || null}
                getOptionLabel={(client) => client.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, client) => chooseClient(client ? String(client.id) : "")}
                disableClearable={clientMode && clientOptions.length === 1}
                disabled={clientMode && clientOptions.length === 1}
                autoHighlight
                openOnFocus
                sx={documentFilterSx}
                renderInput={(params) => <TextField {...params} placeholder="Search clients…" />}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 190 } }}>
              <Box component="label" sx={filterLabelSx}>Pump</Box>
              <Autocomplete
                size="small"
                options={pumps}
                value={pumps.find((pump) => String(pump.id) === pumpId) || null}
                getOptionLabel={(pump) => pump.pumpNumber}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, pump) => {
                  setPumpId(pump ? String(pump.id) : "");
                  resetPage();
                }}
                disabled={!clientId}
                autoHighlight
                openOnFocus
                sx={documentFilterSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder={clientId ? "Search pumps…" : "Select a client"} />
                )}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>Status</Box>
              <FormControl fullWidth size="small" sx={documentFilterSx}>
                <Select
                  value={status}
                  displayEmpty
                  onChange={(event) => {
                    setStatus(event.target.value);
                    resetPage();
                  }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>Pull from</Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={filterText(setDateFrom)}
                sx={documentFilterSx}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>To date</Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={filterText(setDateTo)}
                sx={documentFilterSx}
              />
            </Box>
            {hasActiveFilters ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={clearFilters}
                sx={{ borderColor: "#E2E2DD", borderRadius: "9px", fontSize: 12.5, fontWeight: 650, textTransform: "none" }}
              >
                Clear filters
              </Button>
            ) : null}
          </Box>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {title} could not be loaded.
          </Alert>
        ) : null}
        <Paper elevation={0} sx={{ border: "1px solid #E4E3DC", borderRadius: 2.5, overflow: "hidden" }}>
          <TableContainer>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F8F8F5" }}>
                  {tableHeaders.map((label, index) => (
                    <TableCell
                      key={`${label}-${index}`}
                      align={label === "" ? "right" : "left"}
                      sx={{
                        color: "#707782",
                        fontSize: 11,
                        letterSpacing: 0.65,
                        textTransform: "uppercase",
                        fontWeight: 850,
                        borderColor: "#EAE9E3",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading && !rows.length ? (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 8 }}>
                      <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 36, color: "#B8BDC5", mb: 1 }} />
                      <Typography fontWeight={800}>No pumps found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {clientMode
                          ? "Adjust the filters to find pulled pumps."
                          : "Select a client, adjust filters, or log a new pump pull."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading &&
                  rows.map((row) => (
                    <TableRow key={row.id} hover sx={{ "& td": { borderColor: "#EFEEE9" } }}>
                      <TableCell sx={{ fontWeight: 750 }}>{formatCOIInteger(row.quantity)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={750}>
                          {row.pump?.pumpNumber || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.well?.name || "—"}</TableCell>
                      {!clientMode ? <TableCell>{row.client?.name || "—"}</TableCell> : null}
                      <TableCell>{formatCOIDate(row.pulledDate)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PumpStatusBadge status={row.status} />
                          {canManage ? (
                            <FormControl size="small" sx={{ minWidth: 108 }}>
                              <Select
                                value={row.status}
                                onChange={(event) =>
                                  updateRowStatus(row, event.target.value as PumpEntryStatus)
                                }
                                sx={{ fontSize: 12 }}
                              >
                                {STATUS_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.notes || "—"}
                        </Typography>
                      </TableCell>
                      {canManage ? (
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setEditingRow(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={Number(pagination.totalRecords || 0)}
            page={paginationModel.page}
            rowsPerPage={paginationModel.pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, page) => fetchPaginationModel({ ...paginationModel, page })}
            onRowsPerPageChange={(event) =>
              fetchPaginationModel({ page: 0, pageSize: Number(event.target.value) })
            }
          />
        </Paper>
      </Box>
      <PumpEntryForm
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onSaved={fetchData}
        apiPrefix={apiPrefix}
        clientOptions={clientOptions}
        wellOptions={wellOptions}
        fetchDataWells={fetchDataWells}
        initialClientId={clientId ? Number(clientId) : null}
        lockedClientId={clientMode ? assignedClientId : null}
      />
      <PumpEntryForm
        open={Boolean(editingRow)}
        onClose={() => setEditingRow(null)}
        onSaved={fetchData}
        apiPrefix={apiPrefix}
        clientOptions={clientOptions}
        wellOptions={wellOptions}
        fetchDataWells={fetchDataWells}
        editingRow={editingRow}
      />
    </Box>
  );
};
